import React, { FC, Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Course, TeamGamePlayerDisplay } from '../../../../database.schema';
import { calculateScore } from '../../../utils/score-calculator';

import { BACK_NINE_INDEX, FRONT_NINE_INDEX } from '../../../app/constants';
import { useFetchCourses } from '../../../api/hooks/use-fetch-courses';
import { TeamGamePlayerInfo } from './team-game-player-info';
import { getHoleTierPoints6_6_6 } from '../../../utils/team-games-score-calculator';

const ScoreCardWrapper = styled.div`
  width: 100%;
  background: aliceblue;
  margin-bottom: 1rem;
  padding-top: 1rem;
`;

const TableWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-bottom: 1rem;
`;

const Table = styled.table`
  margin: 0;
  border-collapse: collapse;
`;

const TableData = styled.td`
  padding: 0.25rem;
  border: 1px solid black;
  border-bottom: 1px solid black;
  text-align: center;
  color: black;
  background: white;
`;
const TableClearData = styled.td`
  padding: 0.25rem;
  color: slategray;
  text-align: center;
`;

const TableDataRed = styled(TableData)`
  padding: 0.25rem 0.5rem 0.25rem 0.5rem;
  color: red;
`;

const TableDataSeperator = styled.td`
  background: none;
  height: 0.35rem;
  border: none;
`;

const TableDataPoints = styled(TableData)`
  padding: 0.25rem 0.5rem 0.25rem 0.5rem;
  color: white;
  background: red;
`;

const TableHeader = styled.th<{ colour?: string | null }>`
  color: ${(props) => props.colour || 'black'};
  background-color: #f2f2f2;
  padding: 0.25rem;
  border: 1px solid black;
  text-align: center;
  font-weight: bold;
`;
const TableTotalHeader = styled(TableHeader)`
  border-top: 2px solid black;
  border-bottom: 2px solid black;
`;

const TableTotalHeaderPoints = styled(TableTotalHeader)`
  color: white;
  background: red;
  font-size: 1.2rem;
`;

const ShotText = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 0.25rem 0 0.25rem 1.25rem;
`;

type ScoreCardProps = {
  startingHole: number;
  players: Array<TeamGamePlayerDisplay>;
  courseId: number;
};

export const TierPoints6_6_6_ScoreCard: FC<ScoreCardProps> = ({ startingHole, players, courseId }): JSX.Element => {
  const holeIndexes = startingHole === 1 ? [...FRONT_NINE_INDEX, ...BACK_NINE_INDEX] : [...BACK_NINE_INDEX, ...FRONT_NINE_INDEX];
  const { data } = useFetchCourses();
  const [course, setCourse] = useState<Course>();

  useEffect(() => {
    if (data) {
      const courses = data as Array<Course>;
      const iCourse = courses.find((course) => course.id === courseId);
      if (iCourse) {
        setCourse(iCourse);
      }
    }
  }, [courseId]);
  return (
    <ScoreCardWrapper data-testid="score-card">
      <TeamGamePlayerInfo teams={players} />
      <ShotText>
        <div>* playing handicap 95% of course handicap</div>
      </ShotText>
      <TableWrapper data-testid="score-card">
        <Table data-testid="score-card-table">
          <thead>
            <tr>
              <TableHeader>Hole</TableHeader>
              <TableHeader>SI</TableHeader>
              <TableHeader>Par</TableHeader>
              {players.map((player: TeamGamePlayerDisplay, index: number) => (
                <TableHeader key={`t1-player-${index}`}>{`P${index + 1}`}</TableHeader>
              ))}
              <TableHeader>Pts</TableHeader>
            </tr>
          </thead>
          <tbody>
            {course &&
              holeIndexes.slice(0, 9).map((holeIndex, index) => (
                <Fragment key={`front-9-${index}`}>
                  {index === 0 && (
                    <tr>
                      <TableDataSeperator colSpan={3 + players.length}></TableDataSeperator>
                    </tr>
                  )}
                  <tr data-testid={`front-9-${index}`}>
                    <TableData>{holeIndex + 1}</TableData>
                    <TableDataRed>{course.strokeIndex[holeIndex]}</TableDataRed>
                    <TableData>{course.scoreCard[holeIndex]}</TableData>
                    {players.map((player: TeamGamePlayerDisplay) => (
                      <TableData key={`1-9-t1-${player.nickName}`}>
                        {player.round.scoreCard[index]}
                        <sup>{`${calculateScore({
                          par: course.scoreCard[holeIndex],
                          score: player.round.scoreCard[index],
                          handicap: player.round.individualHandicap,
                          strokeIndex: course.strokeIndex[holeIndex],
                        })}`}</sup>
                      </TableData>
                    ))}
                    <TableDataPoints>{getHoleTierPoints6_6_6(players, index, course, startingHole)}</TableDataPoints>
                  </tr>
                  {index === 5 && (
                    <tr>
                      <TableDataSeperator colSpan={3 + players.length}></TableDataSeperator>
                    </tr>
                  )}
                </Fragment>
              ))}
            <tr>
              <TableClearData></TableClearData>
              <TableClearData></TableClearData>
              <TableClearData></TableClearData>
              <TableClearData></TableClearData>
              {players.map((_, index) => (
                <TableClearData key={`front-clear-${index}`}></TableClearData>
              ))}
            </tr>
            {course && (
              <tr data-testid="out-totals">
                <TableTotalHeader colSpan={2}>Out</TableTotalHeader>
                <TableTotalHeader>{course.scoreCard.slice(0, 9).reduce((acc, hole) => acc + hole, 0)}</TableTotalHeader>
                {players.map((player: TeamGamePlayerDisplay, index: number) => (
                  <TableTotalHeader key={`t19-total-${index}`}>
                    {`${player.round.scoreCard.slice(0, 9).reduce((acc, hole) => acc + hole, 0)}`}
                    <sup>{`${player.round.scoreCard.slice(0, 9).reduce(
                      (acc, hole, index) =>
                        acc +
                        calculateScore({
                          par: course.scoreCard[holeIndexes[index]],
                          score: hole,
                          handicap: player.round.individualHandicap,
                          strokeIndex: course.strokeIndex[holeIndexes[index]],
                        }),
                      0,
                    )}`}</sup>
                  </TableTotalHeader>
                ))}
                <TableTotalHeaderPoints colour="darkorange">
                  {holeIndexes.slice(0, 9).reduce((acc, index) => (index < 9 ? acc + getHoleTierPoints6_6_6(players, index, course, startingHole) : 0), 0)}
                </TableTotalHeaderPoints>
              </tr>
            )}
            <tr>
              <TableClearData></TableClearData>
              <TableClearData></TableClearData>
              <TableClearData></TableClearData>
              <TableClearData></TableClearData>
              {players.map((_, index) => (
                <TableClearData key={`front-clear-${index}`}></TableClearData>
              ))}
              <TableClearData></TableClearData>
            </tr>
            {course &&
              holeIndexes.slice(9).map((holeIndex, index) => (
                <Fragment key={`back-9-${index}`}>
                  <tr data-testid={`back-9-${index}`}>
                    <TableData>{holeIndex + 1}</TableData>
                    <TableDataRed>{course.strokeIndex[holeIndex]}</TableDataRed>
                    <TableData>{course.scoreCard[holeIndex]}</TableData>
                    {players.map((player: TeamGamePlayerDisplay) => (
                      <TableData key={`10-t1-${player.nickName}`}>
                        {player.round.scoreCard[index + 9]}
                        <sup>{`${calculateScore({
                          par: course.scoreCard[holeIndex],
                          score: player.round.scoreCard[index + 9],
                          handicap: player.round.individualHandicap,
                          strokeIndex: course.strokeIndex[holeIndex],
                        })}`}</sup>
                      </TableData>
                    ))}
                    <TableDataPoints>{getHoleTierPoints6_6_6(players, index + 9, course, startingHole)}</TableDataPoints>
                  </tr>
                  {index === 2 && (
                    <tr>
                      <TableDataSeperator colSpan={5 + players.length}></TableDataSeperator>
                    </tr>
                  )}
                </Fragment>
              ))}
            {course && (
              <tr data-testid="in-totals">
                <TableTotalHeader colSpan={2}>In</TableTotalHeader>
                <TableTotalHeader>{course.scoreCard.slice(9).reduce((acc, hole) => acc + hole, 0)}</TableTotalHeader>
                {players.map((player: TeamGamePlayerDisplay, index: number) => (
                  <TableTotalHeader key={`t1-18-total-${index}`}>
                    {`${player.round.scoreCard.slice(9).reduce((acc, hole) => acc + hole, 0)}`}
                    <sup>{`${player.round.scoreCard.slice(9).reduce(
                      (acc, hole, index) =>
                        acc +
                        calculateScore({
                          par: course.scoreCard[holeIndexes[index + 9]],
                          score: hole,
                          handicap: player.round.individualHandicap,
                          strokeIndex: course.strokeIndex[holeIndexes[index + 9]],
                        }),
                      0,
                    )}`}</sup>
                  </TableTotalHeader>
                ))}
                <TableTotalHeaderPoints>
                  {holeIndexes.slice(9).reduce((acc, index) => (index > 8 ? acc + getHoleTierPoints6_6_6(players, index, course, startingHole) : 0), 0)}
                </TableTotalHeaderPoints>
              </tr>
            )}
            <tr>
              <TableClearData></TableClearData>
              <TableClearData></TableClearData>
              <TableClearData></TableClearData>
              <TableClearData></TableClearData>
              {players.map((_, index) => (
                <TableClearData key={`back-clear-${index}`}></TableClearData>
              ))}
              <TableClearData></TableClearData>
            </tr>
            {course && (
              <tr data-testid="overall-totals">
                <TableTotalHeader colSpan={2}>Total</TableTotalHeader>
                <TableTotalHeader>{course.scoreCard.reduce((acc, hole) => acc + hole, 0)}</TableTotalHeader>
                {players.map((player: TeamGamePlayerDisplay, index: number) => (
                  <TableTotalHeader key={`ttt1-9-total-${index}`}>
                    {`${player.round.scoreCard.reduce((acc, hole) => acc + hole, 0)}`}
                    <sup>{`${player.round.scoreCard.reduce(
                      (acc, hole, index) =>
                        acc +
                        calculateScore({
                          par: course.scoreCard[holeIndexes[index]],
                          score: hole,
                          handicap: player.round.individualHandicap,
                          strokeIndex: course.strokeIndex[holeIndexes[index]],
                        }),
                      0,
                    )}`}</sup>
                  </TableTotalHeader>
                ))}
                <TableTotalHeaderPoints>{holeIndexes.reduce((acc, index) => acc + getHoleTierPoints6_6_6(players, index, course, startingHole), 0)}</TableTotalHeaderPoints>
              </tr>
            )}
          </tbody>
        </Table>
      </TableWrapper>
    </ScoreCardWrapper>
  );
};
